import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";

import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Button } from "@progress/kendo-react-buttons";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import CreatePaymentReceipt from "./CreatePaymentReceipt";
import {
  deletePaymentReceipt,
  getAllPaymentReceipt,
} from "./services/paymentreceipt.services";
import {
  clearPaymentReceiptID,
  setPaymentReceiptID,
} from "./paymentreceiptSlice";
import ButtonCreate from "../../components/muidrawer/ButtonCreate";
import AppDrawer from "../../components/muidrawer/Drawer";
import ButtonEdit from "../../components/muidrawer/ButtonEdit";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";

const PaymentReceipt: React.FC = () => {
  const dispatch = useAppDispatch();
  const isDrawerOpen = useAppSelector((state) => state.muidrawer.open);
  const drawerName = useAppSelector((state) => state.muidrawer.name);
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  useEffect(() => {
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <DeletePaymentReceiptDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Payment Receipt List
          </Typography.h4>
          {isAddAccess && (
            <ButtonCreate label="Add New" drawerName="paymentreceiptForm" />
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <PaymentReceiptGridComponent />
        </GridLayoutItem>
        {isDrawerOpen && drawerName === "paymentreceiptForm" && (
          <AppDrawer>
            <CreatePaymentReceipt />
          </AppDrawer>
        )}
      </GridLayout>
    </>
  );
};

/* This function is called when the delete button is clicked */
const DeletePaymentReceiptDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const PaymentReceiptID = useAppSelector(
    (state) => state.paymentreceipt.PaymentReceiptID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deletePaymentReceipt(ID));
      if (response.payload?.Data?.Status === 200) {
        dispatch(getAllPaymentReceipt(0));
        dispatch(closeDialog());
        dispatch(clearPaymentReceiptID());
      } else {
        dispatch(getAllPaymentReceipt(0));
        dispatch(closeDialog());
        dispatch(clearPaymentReceiptID());
      }
    }
  };

  return (
    <>
      {dialogName === "deletePaymentReceipt" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Payment Receipt?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Payment Receipt?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(PaymentReceiptID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const PaymentReceiptGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const PaymentReceiptList = useAppSelector(
    (state) => state.paymentreceipt.PaymentReceiptList
  );
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const loading = useAppSelector((state) => state.paymentreceipt.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? PaymentReceiptList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deletePaymentReceipt"));
      dispatch(setPaymentReceiptID(ID));
    };
    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.payment_guid}
            name="paymentreceiptForm"
            tooltipTitle="Edit"
            themeColor="success"
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() => handleOpenDeleteDialog(props.dataItem.payment_guid)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  // const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
  //   const [IsActive, setIsActive] = useState<boolean>(props.dataItem.isactive);

  //   const handleActiveInActive = () => {
  //     setIsActive(!IsActive);
  //     const data = {
  //       payment_guid: props.dataItem.payment_guid,
  //       isactive: !IsActive,
  //     };
  //     dispatch(PaymentReceiptActiveInactive(data)).then((response: any) => {
  //       if (response.payload?.data?.Status === 200) {
  //         dispatch(getAllPaymentReceipt(0));
  //       } else {
  //         dispatch(getAllPaymentReceipt(0));
  //       }
  //     });
  //   };

  //   return (
  //     <td style={{ textAlign: "center" }}>
  //       <Switch
  //         disabled={isEditAccess ? false : true}
  //         name="IsActive"
  //         checked={IsActive}
  //         onChange={handleActiveInActive}
  //         size="small"
  //       />
  //     </td>
  //   );
  // };

  useEffect(() => {
    dispatch(getAllPaymentReceipt(page?.skip));
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        scrollable="scrollable"
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        filter={filter}
        data={
          filter
            ? filterBy(PaymentReceiptList, filter).slice(
                page.skip,
                page.take + page.skip
              )
            : PaymentReceiptList.slice(page.skip, page.take + page.skip)
        }
        onFilterChange={handleFilterChange}
        skip={page.skip}
        take={page.take}
        total={PaymentReceiptList.length}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 15, "All"],
          pageSizeValue: pageSizeValue,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="receipt_no"
          title="Rceipt No"
          cells={{
            data: (props: any) => (
              <td>
                <ButtonEdit
                  isEditAccess={isEditAccess}
                  ID={props.dataItem.payment_guid}
                  name="paymentreceiptForm"
                  label={props.dataItem?.receipt_no}
                />
              </td>
            ),
          }}
        />
        <Column field="receipt_date" title="Receipt Date" />
        <Column field="karigar_name" title="Karigar Name" />
        <Column field="amount" title="Amount" />
        <Column field="remarks" title="remarks" />
        {/* <Column
          field="payment_guid"
          title="Status"
          cells={{ data: MyActiveInActtiveCommandCell }}
          width={"90px"}
          filterable={false}
          locked={true}
        /> */}
        <Column
          field="payment_guid"
          title="Actions"
          cells={{ data: MyEditCommandCell }}
          width={"110px"}
          filterable={false}
        />
      </KendoGrid>
    </>
  );
};

export default PaymentReceipt;
